import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getSalesforceFieldMappings,
  updateSalesforceFieldMapping,
} from '../api/fieldMappings';
import type {
  SalesforceFieldMapping,
  SalesforceFieldMappingsResponse,
} from '../types';

interface UseSalesforceMappingsOptions {
  /**
   * Gate the field-mappings fetch. Field mappings only exist once connected,
   * so callers should pass `false` until then — otherwise the query fetches
   * prematurely (no connection yet), caches an empty/404 result, and the UI
   * shows "No field mappings found" even after they're created. Defaults to
   * true to preserve the post-setup manager's behavior.
   */
  enabled?: boolean;
}

export function useSalesforceMappings(
  orgId: number | string | undefined,
  options?: UseSalesforceMappingsOptions
) {
  const queryClient = useQueryClient();

  const mappingsQuery = useQuery<SalesforceFieldMappingsResponse>({
    queryKey: ['salesforceMappings', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getSalesforceFieldMappings(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  const updateMapping = useMutation({
    mutationFn: ({
      mappingId,
      body,
    }: {
      mappingId: number;
      body: Parameters<typeof updateSalesforceFieldMapping>[2];
    }) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updateSalesforceFieldMapping(orgId, mappingId, body);
    },
    onSuccess: (_data: SalesforceFieldMapping) =>
      queryClient.invalidateQueries({
        queryKey: ['salesforceMappings', orgId],
      }),
  });

  return useMemo(
    () => ({
      mappings: mappingsQuery,
      isLoading: mappingsQuery.isLoading,
      error: mappingsQuery.error,
      updateMapping,
    }),
    [mappingsQuery, updateMapping]
  );
}
